<script>
import {mapGetters} from "vuex";
import moment from "moment/moment";
import Swal from "sweetalert2";

export default {
    name: "list-table",

    props: {
        updateKey: {
            type: Number
        }
    },

    computed: {
        ...mapGetters({
            messages: 'sms/toList',
            loading: 'sms/loading',
            paginator: 'sms/toListPager',
        }),

        fields: function () {
            return [
                {
                    key: "id",
                    label: '#',
                    thClass: 'gridjs-th',
                    sortable: true
                },
                {
                    key: "contact_list_name",
                    label: this.$t('contact.contact_list'),
                    thClass: 'gridjs-th',
                    sortable: true
                },
                {
                    key: "status",
                    label: this.$t('sms.status'),
                    thClass: 'gridjs-th border-left-0',
                    formatter: (value) => this.$t('sms.' + value?.toLowerCase()),
                    sortable: true
                },
                {
                    key: "create_date",
                    label: this.$t('sms.create_date'),
                    thClass: 'gridjs-th border-left-0',
                    formatter: (value) => {
                        return moment(value).format('YYYY. MM. DD. HH:mm')
                    },
                    sortable: true
                },
                {
                    key: "completed",
                    label: this.$t('sms.completed'),
                    thClass: 'gridjs-th border-left-0',
                    tdClass: 'text-start',
                    sortable: true
                },
                {
                    key: "actions",
                    label: '',
                    thClass: 'gridjs-th',
                    tdClass: 'text-end'
                },
            ];
        },
    },

    data: function () {
        return {
            sortBy: (this.paginator && this.paginator.column) || 'id',
            sortDesc: (this.paginator && this.paginator.desc) || true,
        };
    },

    methods: {
        toPage: function (val) {
            this.page = val
            this.delayedFetch()
        },

        delayedFetch: function () {
            this.$store.dispatch('sms/fetchToListDelayed', {
                limit: this.perPage,
                offset: this.pager && this.pager.offset,
                page: this.page,
                sorting: {
                    column: this.sortBy,
                    desc: this.sortDesc
                }
            });
        },

        showMessage: function (message) {
            Swal.fire({
                html: message,
                buttonsStyling: false,
                customClass: {
                    confirmButton: 'btn btn-success btn-md'
                }
            })
        },

        revoke: function (id) {
            Swal.fire({
                title: this.$t('table.confirm'),
                text: this.$t('sms.confirm_revoke'),
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                customClass: {
                    confirmButton: 'btn btn-warning btn-md me-1',
                    cancelButton: 'btn btn-primary btn-md ms-1',
                },
                confirmButtonText: this.$t('table.yes'),
                cancelButtonText: this.$t('table.no')
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$store.dispatch('sms/revokeSentToList', id).then(() => {
                        Swal.fire(
                            this.$t('sms.message_revoked'),
                            this.$t('sms.selected_item_is_revoked'),
                            'success'
                        )
                    });
                }
            })
        },

        getTitle: function (item) {
            if (item.status === 'WAITING') {
                return this.$t('sms.create_date');
            }

            if (item.status === 'CANCELLED' && item.deleted_date) {
                return this.$t('sms.cancelled') + ' (' + item.deleted_date.formattedDate() + ')';
            }

            return '';
        },
    },

    watch: {
        sortBy: function () {
            this.delayedFetch();
        },
        sortDesc: function () {
            this.delayedFetch();
        },
        updateKey: function () {
            this.delayedFetch();
        },
    },

    created() {
        this.$store.dispatch('sms/fetchToList');
    },
}
</script>

<template>
    <div class="list-table-wrapper">
        <div class="table-responsive mb-0">
            <b-table
                :items="messages"
                :fields="fields"
                responsive="sm"
                :per-page="paginator.perPage"
                :current-page="paginator.currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                show-empty
                :empty-text="$t('table.please_select_or_empty')"
                hover
                head-variant="light"
                :busy="loading"
                no-local-sorting
            >
                <template #table-busy>
                    <div class="text-center">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">{{ $t('table.loading') }}...</span>
                        </div>
                    </div>
                </template>
                <template #cell(status)="scope">
                    <div :title="getTitle(scope.item)">
                        <i v-if="scope.unformatted === 'WAITING'" class="fas fa-hourglass-half"></i>
                        <i v-if="scope.unformatted === 'CANCELLED'" class="fas fa-trash-alt text-danger"></i>
                        <i v-if="scope.unformatted === 'SENT' || scope.unformatted === 'OK'" class="fas fa-check-circle text-success"></i>
                        <i v-if="scope.unformatted === 'EXP'" class="fas fa-exclamation-circle text-danger"></i>
                        <span class="ms-2">{{ scope.value }}</span>
                    </div>
                </template>
                <template #cell(completed)="scope">
                    <strong :class="{'text-success': scope.item.percent >= 70, 'text-warning': scope.item.percent < 70 && scope.item.percent >= 33, 'text-danger': scope.item.percent < 33}" style="font-size: 20px;">{{ scope.item.sent_sms }}</strong>
                    <span style="margin: 0 2px 0">/</span>
                    <small>{{ scope.item.total }}</small>
                    <span class="badge ms-3" :class="{'text-bg-success': scope.item.percent >= 70, 'text-bg-warning': scope.item.percent < 70 && scope.item.percent >= 33, 'text-bg-danger': scope.item.percent < 33}">{{ scope.item.percent }}%</span>
                </template>
                <template #cell(is_deleted)="scope">
                    <i v-if="Number(scope.value) === 1" class="fa fa-trash-alt text-danger"></i>
                </template>
                <template #cell(actions)="scope">
                    <div class="d-inline-flex">
                        <b-button class="me-2" v-b-tooltip.hover.top="$t('sms.message')" size="sm" variant="primary" @click="showMessage(scope.item.sms)">
                            <i class="fas fa-comment"></i>
                        </b-button>
                        <b-button v-if="Number(scope.item.remaining_sms) > 0 && scope.item.is_deleted != 1" v-b-tooltip.hover.top="$t('sms.revoke')" size="sm" variant="danger" @click="revoke(scope.item.id)">
                            <i class="fas fa-times-circle"></i>
                        </b-button>
                        <span v-else class="d-inline-block" style="width: 31px; height: 29px;"></span>
                    </div>
                </template>
            </b-table>
        </div>
        <div class="row mb-3 mx-2">
            <div class="col">
                <div
                    class="dataTables_paginate paging_simple_numbers float-end"
                >
                    <ul class="pagination pagination-rounded mb-0">
                        <!-- pagination -->
                        <b-pagination
                            v-model="paginator.current_page"
                            :limit="paginator.limit"
                            :total-rows="paginator.count"
                            :per-page="paginator.limit"
                            @change="toPage"
                        ></b-pagination>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>